<template>
  <div class="h-screen overflow-hidden flex flex-col bg-white border-solid border-l border-0 border-gray-border">
    <form @submit="createIndex" class="flex flex-col h-full">
      <div v-bar class="flex-1 min-h-0 overflow-y-auto">
        <div class="min-h-full">
          <h3 class="mt-8 font-semibold px-8">Create New Knowledge Base {{ showUnreleased ? 'Group' : '' }}</h3>

        <div class="mt-3">
          <with-error-message :show-error="isIndexNameError" class="border-solid border-b border-0 border-gray-border px-8 pb-5">
            <base-label id="indexName" :label="'Knowledge Base ' + (showUnreleased ? 'Group Name' : 'Name')" :label-classes="'font-medium'"/>
            <base-input id="indexName"
                        name="indexName"
                        placeholder="e.g. Sales campaign"
                        variant="editor"
                        type="string"
                        class="w-full border-gray-border mt-1 h-10"
                        :class="{'border-focus-error': isIndexNameError}"
                        v-model.trim="indexName"
            />
          </with-error-message>

          <settings-bot-market-form v-if="showUnreleased"
                                    v-model="marketForm"
                                    content-class="px-8 pb-3"
          />

          <div class="mt-3" :class="{'border-solid border-0 border-t border-gray-border pt-4': showUnreleased}">
            <div class="px-8">
              <with-error-message :show-error="isIndexSourceNameError">
                <base-label id="indexSourceName" label="Source Name" :label-classes="'font-medium'"/>
                <base-input id="indexSourceName"
                            name="indexSourceName"
                            placeholder="e.g. Sales campaign feed"
                            variant="editor"
                            type="string"
                            class="w-full border-gray-border py-px mt-1 h-10"
                            :class="{'border-focus-error': isIndexSourceNameError}"
                            v-model.trim="indexSourceName"
                />
              </with-error-message>

              <div class="mt-5">
                <base-label label="Data source"
                            :label-classes="'font-medium'"
                            id="data-source"
                />
                <BaseToggleGroup class="mt-1" :tabs="formsOptions" v-model="sourceType" />
                <settings-indexes-forms class="mt-4"
                                        :fileSettings.sync="fileSettings"
                                        :active-form.sync="sourceType"
                                        :xmlFeedSettings.sync="xmlFeedSettings"
                                        :webPageSettings.sync="webPageSettings"
                                        :show-vectorization-skip-tags="providerForm.provider !== 'qdrant'"
                                        @update:available-tags="availableTags = $event"
                />
              </div>

              <settings-index-provider v-if="showUnreleased"
                                       v-model="providerForm"
                                       class="mt-5"
              />

              <settings-index-field-selection v-if="showFieldSelection"
                                              :available-tags="availableTags"
                                              :value="xmlFeedSettings.fieldSelection"
                                              @input="updateFieldSelection"
                                              class="mt-5"
              />
            </div>

            <settings-index-scheduler :schedule-params="scheduleParams"
                                      :is-create="true"
                                      @update-schedule-params="updateScheduleParams"
                                      class="mt-6 pt-4 px-8 pb-8 border-solid border-t border-0 border-gray-border"
            />
          </div>
        </div>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4 mb-6 px-8 flex-shrink-0">
        <busy-button id="createButton"
                     :variant="createButtonVariant"
                     class="col-span-1"
                     :is-loading="isLoading"
                     :is-disabled="isCreateDisabled"
                     @click="createIndex"
        >
          <span class="w-full">{{ createButtonText }}</span>
        </busy-button>
      </div>
    </form>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import SettingsBotMarketForm from '@/components/settings/bots/SettingsBotMarketForm.vue'
import SettingsIndexesXmlFeed from '@/components/settings/indexes/SettingsIndexesXmlFeed.vue'
import SettingsIndexScheduler from '@/components/settings/indexes/SettingsIndexScheduler.vue'
import indexGroupService from '@/services/amio/index-group.service'
import mixins from '@/utils/mixins'
import indexCreateMixin from '@/utils/index-create.mixin'
import {
  createDefaultMarketForm,
  isMarketFormValid,
  resolveMarketId
} from '@/utils/market.utils'
import marketModule from '@/store/modules/market.module'
import SettingsIndexesForms from '@/components/settings/indexes/SettingsIndexesForms.vue'
import SettingsIndexProvider from '@/components/settings/indexes/SettingsIndexProvider.vue'
import SettingsIndexFieldSelection from '@/components/settings/indexes/SettingsIndexFieldSelection.vue'
import WithErrorMessage from '@/components/common/WithErrorMessage.vue'
import { KNOWLEDGE_BASES } from '@/utils/knowledgeBase.constants'

export default {
  name: 'SettingsIndexCreate',
  mixins: [mixins, indexCreateMixin],
  components: {
    SettingsBotMarketForm,
    SettingsIndexesForms,
    SettingsIndexProvider,
    SettingsIndexFieldSelection,
    BusyButton,
    SettingsIndexesXmlFeed,
    SettingsIndexScheduler,
    WithErrorMessage
  },
  props: {
    indexes: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      marketForm: createDefaultMarketForm(),
      indexName: '',
      providerForm: { provider: 'azure' },
      availableTags: []
    }
  },
  computed: {
    isIndexNameError() {
      return this.errorDescription?.includes('Knowledge Base Name')
    },

    isMarketFormValid() {
      return isMarketFormValid(this.marketForm, marketModule.getMarketList().length > 0)
    },
    isCreateDisabled() {
      if (this.isLoading) return true
      if (this.showUnreleased && !this.isMarketFormValid) return true
      return !this.indexName
    },
    // Per-field embed/payload/group-key selection only applies to a Qdrant XML feed once the
    // preview has surfaced the feed's fields.
    showFieldSelection() {
      return this.showUnreleased &&
        this.providerForm.provider === 'qdrant' &&
        this.sourceType === KNOWLEDGE_BASES.XML_FEED &&
        this.availableTags.length > 0
    }
  },
  watch: {
    // drop any field-selection state once it no longer applies (provider/source switched away from a
    // Qdrant XML feed) so an Azure create can't carry stale embed/payload/group_key keys
    showFieldSelection(isShown) {
      if (!isShown) this.updateFieldSelection({ embed_fields: [], payload_fields: {}, group_key: [] })
    }
  },
  methods: {
    updateFieldSelection(fieldSelection) {
      this.xmlFeedSettings = { ...this.xmlFeedSettings, fieldSelection }
    },

    async resolveIndexCreateParams() {
      if (!this.showUnreleased) {
        return { indexName: this.indexName, extraParams: { ...this.providerForm }, createdIndexGroupId: null }
      }

      const indexGroup = await indexGroupService.create(this.indexName)
      try {
        const { marketId, marketName } = await resolveMarketId(this.marketForm)
        return {
          indexName: marketName,
          extraParams: {
            index_group_id: indexGroup.id,
            market_id: marketId,
            ...this.providerForm
          },
          createdIndexGroupId: indexGroup.id
        }
      } catch (err) {
        await this.rollbackIndexCreate(indexGroup.id)
        throw err
      }
    },

    async rollbackIndexCreate(indexGroupId) {
      if (!indexGroupId) return
      try {
        await indexGroupService.delete(indexGroupId)
      } catch {
        // Best-effort cleanup; the original error is still surfaced to the user
      }
    }
  }
}
</script>
